import { CommonModule } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import { Component, Input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { BehaviorSubject } from 'rxjs';

import { NgTemplateAssertType } from '@core/common-legacy';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import { type AnySymbol, SymbolComponent } from '@core/symbol-legacy';
import type { Select2Item } from '@core/user-interface-legacy';
import {
  MultiselectFeatureDirective,
  SearchFeatureDirective,
  Select2Component,
  Select2SelectedItemDirective,
  ChipsComponent,
  AvatarComponent,
  SelectedItemCounterDirective,
  Select2ListItemDirective,
  CheckboxComponent,
  DropdownItemComponent,
  AlwaysOpenStrategyDirective,
  Select2TriggerDirective,
  IconComponent,
  MultiSelectTriggerComponent,
} from '@core/user-interface-legacy';
import { getInitials, injectUntilDestroyed } from '@core/utils-legacy';
import type { InputOptionsState, OptionsQueryParams } from '@core/views-data';
import type { OptionToValue } from '@core/views-types';

import type { InputMultiSelectFormValue, InputMultiSelectOptions } from './types';
import type { InputTypeComponent } from '../../advanced-filters/filter/types';

interface MultiselectItem extends Select2Item<OptionToValue<InputMultiSelectOptions>> {
  imageUrl?: string;
  symbol?: AnySymbol | null;
  initials?: string;
}

export type MultiSelectInputState = MultiselectItem[];

type InputMultiSelect = InputTypeComponent<
  FormControl<InputMultiSelectFormValue | null>,
  MultiSelectInputState,
  InputMultiSelectOptions
>;

export const optionToMultiSelectItem = (option: InputMultiSelectOptions): MultiselectItem => {
  if (option.type === 'plain') {
    return {
      label: option.label.text,
      value: <OptionToValue<InputMultiSelectOptions>>{ ...option.value, type: option.type },
      symbol: option.label.symbol ?? null,
    };
  } else if (option.type === 'job_status' || option.type === 'work_model') {
    return {
      label: option.label.text,
      value: <OptionToValue<InputMultiSelectOptions>>{ ...option.value, type: option.type },
    };
  } else {
    return {
      label: option.label.text,
      value: { ...option.value, type: option.type },
      imageUrl: option.label.avatarUrl,
      initials: getInitials(option.label.text),
    };
  }
};

@Component({
  selector: 'rt-views-input-multiselect',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Select2Component,
    SearchFeatureDirective,
    MultiselectFeatureDirective,
    Select2SelectedItemDirective,
    SelectedItemCounterDirective,
    ChipsComponent,
    AvatarComponent,
    Select2ListItemDirective,
    CheckboxComponent,
    DropdownItemComponent,
    AlwaysOpenStrategyDirective,
    Select2TriggerDirective,
    OfferStatusIndicatorComponent,
    NgTemplateAssertType,
    IconComponent,
    MultiSelectTriggerComponent,
    SymbolComponent,
  ],
  templateUrl: './input-multiselect.component.html',
  styleUrls: ['./input-multiselect.component.less'],
})
export class InputMultiselectComponent implements InputMultiSelect {
  public readonly LOAD_MORE_WHEN_LEFT = 5;
  public readonly TMultiselectItem: MultiselectItem;
  public readonly WORK_MODEL_ICON_MAP = <const>{
    remote: 'nav-careershub',
    hybrid: 'hybrid',
    on_site: 'domain',
  };

  @Input() public inputControl: InputMultiSelect['inputControl'];
  @Input() public optionsData: InputMultiSelect['optionsData'];
  @Input() public mode: 'quick' | 'advanced';
  @Input() public state: MultiSelectInputState;
  @Input() public placeholder: string;

  public search = output<string>();
  public loadMore = output<OptionsQueryParams>();
  public closed = output<void>();
  public chipRemoved = output<void>();
  public clear = output<void>();

  public state$ = new BehaviorSubject<MultiSelectInputState | null>(null);
  public optionOptions: MultiselectItem[];
  public preselected: MultiselectItem[];
  public searchControl = new FormControl('', { nonNullable: true });
  public type: MultiselectItem['value']['type'] = 'plain';

  private untilDestroyed = injectUntilDestroyed();

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.state) {
      this.preselected = this.state;
      this.state$.next(this.state);
    }

    if (changes.optionsData) {
      this.optionOptions = this.optionsData ? this.getOptions(this.optionsData) : [];
      this.type = this.optionOptions[0]?.value.type ?? 'plain';
    }
  }

  public ngOnInit() {
    this.searchControl.valueChanges
      .pipe(this.untilDestroyed)
      .subscribe(query => this.search.emit(query));
  }

  public onScrollChange(event: IPageInfo) {
    if (!this.optionsData) return;

    const { page, limit } = this.optionsData.meta;
    const isCloseToScrollEnd =
      event.endIndexWithBuffer > this.optionOptions.length - this.LOAD_MORE_WHEN_LEFT;
    const canLoadMore = page * limit === this.optionOptions.length;
    if (isCloseToScrollEnd && canLoadMore) {
      this.loadMore.emit({
        page: page + 1,
        limit: limit,
        searchQuery: this.searchControl.value,
      });
    }
  }

  private getOptions({ options }: InputOptionsState<InputMultiSelectOptions>): MultiselectItem[] {
    return options.map(option => optionToMultiSelectItem(option));
  }
}
